import { useEffect } from "react";
import { getConsent } from "./CookieConsent";
import { api } from "../lib/api";

/**
 * MonetagLoader — charge dynamiquement les scripts publicitaires Monetag
 * selon la config admin (mode granulaire par format).
 *
 * Mode "granular" (nouveau, recommandé) :
 * - Charge UN script par format actif (in_page_push, vignette, popunder, native_banner)
 * - Popunder désactivé côté admin ? → aucun script popunder ne se charge → pas de pub OnClick
 *
 * Mode "efficient" (legacy) :
 * - Si aucun format granulaire n'est configuré, on retombe sur le script global (script_zone)
 *   qui laisse Monetag décider automatiquement.
 *
 * Idempotent : chaque script est guardé par window.__monetag_loaded_<key>__.
 */
const MONETAG_HOST = "https://nap5k.com/tag.min.js";

function loadZoneScript(key, zoneId) {
    const flag = `__monetag_loaded_${key}__`;
    if (window[flag]) return;
    if (!zoneId) return;
    window[flag] = true;
    const s = document.createElement("script");
    s.async = true;
    s.setAttribute("data-cfasync", "false");
    s.setAttribute("data-testid", `monetag-${key}`);
    s.dataset.zone = String(zoneId);
    s.src = MONETAG_HOST;
    document.head.appendChild(s);
}

export default function MonetagLoader() {
    useEffect(() => {
        let cancelled = false;
        const consent = getConsent();
        if (!consent?.advertising) return;

        (async () => {
            try {
                const { data } = await api.get("/public/monetag-status");
                if (cancelled) return;
                if (!data?.enabled) return;

                if (data.mode === "granular" && data.formats) {
                    // Charge un script par format actif — popunder skip si off.
                    // Native Banner exclu : géré par MonetagSlot (per-instance).
                    Object.entries(data.formats).forEach(([fmtKey, zoneId]) => {
                        if (fmtKey === "native_banner") return;
                        if (zoneId) loadZoneScript(fmtKey, zoneId);
                    });
                } else if (data.script_zone) {
                    // Legacy Efficient Tag (charge tous les formats configurés côté Monetag)
                    loadZoneScript("efficient", data.script_zone);
                }
            } catch {
                /* backend down → on ne charge rien, silencieux */
            }
        })();

        const handler = () => {
            const c = getConsent();
            if (!c?.advertising) {
                document.querySelectorAll('script[data-testid^="monetag-"]').forEach((el) => el.remove());
                ["efficient", "in_page_push", "vignette", "popunder", "native_banner"].forEach((k) => {
                    delete window[`__monetag_loaded_${k}__`];
                });
            }
        };
        window.addEventListener("mboa-consent-change", handler);
        return () => {
            cancelled = true;
            window.removeEventListener("mboa-consent-change", handler);
        };
    }, []);

    return null;
}
